import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type Requirement } from "@src/appV2/Requirement/types";
import { type UseQueryResult } from "@tanstack/react-query";

import { Resources } from "../types/resources";
import {
  type GetDocumentsRequestDto,
  getDocumentsRequestSchema,
  type GetDocumentsResponse,
  getDocumentsResponseSchema,
  type WorkerDocument,
} from "../types/workerDocument.types";

const STALE_TIME_IN_MS = 60 * 2 * 1000; // 2 mins

export const WORKER_DOCUMENTS_URL = `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/documents`;

/**
 * Prefix key for invalidating worker-documents queries: entries are stored under
 * `[url, queryParams]`, so `[url]` matches every variant.
 */
export function getWorkerDocumentsQueryKey(): readonly unknown[] {
  return [WORKER_DOCUMENTS_URL] as const;
}

export interface UseGetWorkerDocumentsOptions extends UseGetQueryOptions<
  GetDocumentsResponse,
  WorkerDocument[]
> {
  customHeaders?: Record<string, string>;
}

export function useGetWorkerDocuments(
  queryParams: GetDocumentsRequestDto,
  options: UseGetWorkerDocumentsOptions = {}
): UseQueryResult<WorkerDocument[]> {
  return useGetQuery<GetDocumentsRequestDto, GetDocumentsResponse, WorkerDocument[]>({
    url: WORKER_DOCUMENTS_URL,
    queryParams,
    requestSchema: getDocumentsRequestSchema,
    responseSchema: getDocumentsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_DOCUMENTS_FAILURE,
    },
    staleTime: STALE_TIME_IN_MS,
    // Inject the requirement object directly in the worker document for easier access
    select: (data: GetDocumentsResponse): WorkerDocument[] => {
      const requirementsMap = new Map<string, Requirement>();
      if (isDefined(data?.included)) {
        data.included.forEach((item) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
          if (item.type === Resources.REQUIREMENTS) {
            requirementsMap.set(item.id, item);
          }
        });
      }

      return data.data.map((document) => {
        const requirement = requirementsMap.get(document.attributes.requirementId);
        return {
          ...document,
          attributes: {
            ...document.attributes,
            requirement,
          },
        };
      });
    },
    ...options,
  });
}
